import uPlot from "uplot";
import { PatientVisit } from "../../amplify/api-gateway/search-patients";
import { PLOT_BLUE, PLOT_RED, PLOT_YELLOW, seriesPointsPlugin, tooltipNumericPlugin, tooltipTimelinePlugin, verticalAnnotationsPluginForWaveforms, verticalAnnotationsPluginForNumerics, verticalAnnotationsPluginForTimeline, wheelZoomPlugin, tooltipWaveformAvailabilityPlugin, PLOT_GRAY } from "../../utils/plot";
import { getEarliestTime, getLatestTime } from "../../utils/time";

export const PLOT_HEIGHT = 140;
export const PREFERRED_NUMERICS_ORDER = ["HR", "NBP", "SpO2", "RR", "Temp", "HRV", "Perf"];
export const PREFERRED_ECG_NUMERICS_ORDER = ["QTc"];
export const PREFERRED_WAVEFORMS_ORDER = ["II", "Pleth", "Resp"];

export interface SelectedTimelineMinMax {
    min?: Date,
    max?: Date,
}

function formatAnnotationsPluginMarks(patientVisit: PatientVisit) {
    function formatSingleMark(shortName: string, timeString: string, label: string) {
        return {
            type: shortName,
            from: new Date(timeString).getTime() / 1000,
            to: new Date(timeString).getTime() / 1000 + 60,
            label: label,
        };
    }

    const marks: any[] = [];
    if (patientVisit.arrival_time) {
        marks.push(formatSingleMark('arrival', patientVisit.arrival_time, 'Arrival'));
    }
    if (patientVisit.roomed_time) {
        marks.push(formatSingleMark('roomed', patientVisit.roomed_time, 'Roomed'));
    }
    if (patientVisit.admit_time) {
        marks.push(formatSingleMark('admit', patientVisit.admit_time, 'Admitted to Inpatient'));
    }
    if (patientVisit.dispo_time) {
        marks.push(formatSingleMark('dispo', patientVisit.dispo_time, 'Dispo'));
    }
    if (patientVisit.departure_time) {
        marks.push(formatSingleMark('departure', patientVisit.departure_time, 'Departure'));
    }
    return marks;
}

function formatAnnotationsPluginOpts(patientVisit: PatientVisit) {
    function formatSingleOpt() {
        return {
            show: true,
            width: 2,
            align: 'btm',
            stroke: 'rgb(45 181 181)',
            fill: 'rgb(45 181 181 / 20%)',
            dash: [5, 5],
        };
    }

    const opts: any = {};
    if (patientVisit.arrival_time) {
        opts['arrival'] = formatSingleOpt();
    }
    if (patientVisit.roomed_time) {
        opts['roomed'] = formatSingleOpt();
    }
    if (patientVisit.admit_time) {
        opts['admit'] = formatSingleOpt();
    }
    if (patientVisit.dispo_time) {
        opts['dispo'] = formatSingleOpt();
    }
    if (patientVisit.departure_time) {
        opts['departure'] = formatSingleOpt();
    }
    return opts;
}

export function getTimelineOpts(addAnnotationToPlots: any, setLabsModalHighlightedTime: any, handleShowCustomAnnotationModal: any, handleShowOtherAnnotationModal: any, handleShowTimelineContextMenu: any, plotWidth: number, plotHeight: number, patientVisit: PatientVisit, setSelectedTimelineMinMax: any, lockTimelineZoom: boolean, tenant: string) {
    const opt: any = {
        id: `timeline`,
        width: plotWidth,
        height: plotHeight - 30, // subtract to accommodate the spacing around plot
        focus: false,
        legend: {
            show: false
        },
        cursor: {
            y: false,
            focus: {
                prox: 5,
            },
            ...(lockTimelineZoom ? {
                drag: {
                    setScale: false,
                    x: true, 
                    y: false,
                },
            } : {}),
            sync: {
                key: 'numerics',
            },
        },
        scales: {
            x: {
                time: true,
                min: patientVisit ? getEarliestTime(patientVisit) / 1000 : new Date().getTime() / 1000,
                max: patientVisit ? getLatestTime(patientVisit, tenant) / 1000 : new Date().getTime() / 1000,
            },
            y: {
                range: [-0.25, 2.25],
            }
        },
        axes: [{
        }, {
            show: true,
            incrs: [
                1
            ],
            label: "Events",
            labelGap: 8,
            grid: {
                show: false,
            },
            values: (u: any, t: any) => t.map((v: number) => {
                // if (v === 0) {
                //     return "Labs";
                // } else if (v === -1) {
                //     return "Meds";
                // } else {
                //     return "";
                // }
                return "";
            }),
        }],
        series: [
            {},
            { // LABS
                paths: () => null, // Disables line
                points: {
                    fill: PLOT_YELLOW, // fill color on mouseover
                },
                stroke: PLOT_YELLOW,
                width: 4,
            },
            { // MEDS
                paths: () => null, // Disables line
                points: {
                    fill: PLOT_YELLOW, // fill color on mouseover
                },
                stroke: PLOT_YELLOW,
                width: 4,
            },
            { // USER ANNOTATIONS
                paths: () => null, // Disables line
                points: {
                    fill: PLOT_YELLOW, // fill color on mouseover
                },
                stroke: PLOT_YELLOW,
                width: 4,
            },
            { // OTHER 
                paths: () => null, // Disables line
                points: {
                    fill: PLOT_YELLOW, // fill color on mouseover
                },
                stroke: PLOT_YELLOW,
                width: 4,
            },
        ],
        plugins: [
            seriesPointsPlugin(),
            tooltipTimelinePlugin(addAnnotationToPlots, setLabsModalHighlightedTime, handleShowCustomAnnotationModal, handleShowOtherAnnotationModal, handleShowTimelineContextMenu, patientVisit),
            verticalAnnotationsPluginForTimeline(formatAnnotationsPluginMarks(patientVisit), {
                types: formatAnnotationsPluginOpts(patientVisit),
                showAnnotationLabel: true,
            }),
        ],
        hooks: {
            init: [
                (u: any) => {
                    u.over.onmouseup = (e: any) => {
                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }
                    }
                }
            ],
            setSelect: [
                (u: uPlot) => {
                    console.log("Set select called on timeline");
                    const sel = u.root.querySelector(".u-select") as HTMLElement;
                    if (sel) {
                        // TODO: This stopped working since we have disabled the draggable bounding box
                        //
                        // The bounding rectangle for some reason does not give the correct coordinates.
                        // const left = sel.getBoundingClientRect().left;
                        // const right = sel.getBoundingClientRect().right;
                        const left = parseFloat(sel.style.left.replace("px", ""));
                        const width = parseFloat(sel.style.width.replace("px", ""));
                        const right = left + width;
                        const leftTime = u.posToVal(left, 'x');
                        const rightTime = u.posToVal(right, 'x');
                        // console.log(`left=${left} right=${right}`);
                        // console.log(new Date(leftTime * 1000), new Date(rightTime * 1000));
                        if (leftTime < rightTime) {
                            // console.log("setSelectedTimelineMinMax", leftTime, rightTime)
                            setSelectedTimelineMinMax({
                                min: new Date(leftTime * 1000),
                                max: new Date(rightTime * 1000),
                            });
                        }
                    }
                },
            ]
        },
    }

    return opt;
}

/**
 * If there are isolated points in the numerics, we want to make sure we show the points with 
 * a visible icon (otherwise it will be hidden by the point density control). 
 * Example: we want the point in Graph 1 to show up.
 * Graph 1:         *
 * Graph 2: *****************
 * 
 * @param gaps: Looks like the following:
 *  [[100, 212], [216, 443], [443, 674], [674, 1656]]
 */
function showPointsInCaseOfOverlappingGaps(gaps: number[][]) {
    if (gaps) {
        for (let i=0; i < gaps.length - 1; i++) {
            if (gaps[i][1] === gaps[i + 1][0]) {
                return true;
            }
        }
        return false;
    } else {
        return false;
    }
}

export function getNumericOpts(plotWidthRef: any, numeric: string, idx: number, handleShowContextMenu: any, plotWidth: number | null, minMax: any, updateWaveformPreview: any, patientVisit: any, showXAxis?: boolean) {
    let seriesOpts = [
        {}, // Time
        {
            // paths: () => null, // Disables line
            stroke: PLOT_BLUE,
            spanGaps: true,
            dash: [1, 3], 
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: PLOT_BLUE,
            },
        }, // Charted Vital Signs #1
        {
            // paths: () => null, // Disables line
            stroke: PLOT_BLUE,
            spanGaps: true,
            dash: [1, 3], 
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: PLOT_BLUE,
            },
        }, // Charted Vital Signs #2
        {
            stroke: PLOT_BLUE,
            spanGaps: true,
            points: {
                // There doesn't appear to be a way to get infrequent points to show up 
                // but hide more frequent points because infrequent points are surrounded
                // by 'null' values and the 'space' parameter doesn't seem to care what
                // values are in the dataset when determining what points to show.
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
                // show: true,
                // space: 1,
            },
        }, // Monitor Vital Signs #1
        {
            stroke: PLOT_BLUE,
            spanGaps: true,
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Monitor Vital Signs #2
        {
            paths: () => null, // Disables line
            stroke: 'red',
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: "red",
            },
        }, // Abnormal Charted Vital Signs #1
        {
            paths: () => null, // Disables line
            stroke: 'red',
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: "red",
            },
        }, // Abnormal Charted Vital Signs #2
        {
            stroke: 'red',
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Abnormal Monitor Vital Signs #1
        {
            stroke: 'red',
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Abnormal Monitor Vital Signs #2
    ];

    return {
        id: `${idx}-${numeric}`,
        width: plotWidth ? plotWidth : plotWidthRef.current,
        height: showXAxis ? PLOT_HEIGHT + 40 : PLOT_HEIGHT,
        legend: {
            show: false
        },
        plugins: [
            verticalAnnotationsPluginForNumerics({
                types: {
                    eventMark: {
                        show: true,
                        width: 2,
                        align: 'btm',
                        stroke: 'rgb(45 181 181)',
                        fill: 'rgb(45 181 181 / 20%)',
                        dash: [5, 5],
                    },
                    waveformMark: {
                        show: true,
                        width: 2,
                        align: 'btm',
                        stroke: 'rgb(76 175 80)',
                        fill: 'rgb(76 175 80 / 20%)',
                        dash: [5, 5],
                    },
                },
                showAnnotationLabel: true,
            }),
            tooltipNumericPlugin(),
            // wheelZoomPlugin({factor: 0.75}),
        ],
        cursor: {
            sync: {
                key: 'numerics',
            },
        },
        scales: {
            x: {
                time: true,
                // If the plot is zoomed in, we want to retain the zoom on any changes to the plot
                // such as the addition of new annotations.
                //
                // @ts-ignore
                min: globalThis.zoomedRange ? globalThis.zoomedRange.min : minMax.min,
                // @ts-ignore
                max: globalThis.zoomedRange ? globalThis.zoomedRange.max : minMax.max,
            },
            ...(numeric === "Temp" ? {
                y: {
                    range: (self: any, dataMin: number, dataMax: number) => {return [dataMin - 0.25, dataMax + 0.25]},
                }
            } : {}),
            // ...(numeric === "Temp" ? {y: { range: [tempUnit === "F" ? 90 : 32, tempUnit === "F" ? 105 : 40] }} : {}),
        },
        series: seriesOpts,
        axes: [
            {
                show: true,
                font: showXAxis ? "12px Arial" : "0px Arial",
                labelSize: showXAxis ? 30 : 0,
                size: showXAxis ? 50 : 4,
            },
            {
                show: true,
                // label: numeric,
                labelGap: 8,
            },
        ],
        hooks: {
            init: [
                (u: any) => {
                    u.over.onmouseup = (e: any) => {

                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }
                    }
                    u.over.onclick = (e: any) => {
                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }

                        let clickedTimestamp = u.posToVal(e.offsetX, 'x');
                        updateWaveformPreview(new Date(clickedTimestamp * 1000));

                        // Move the waveform preview to where the user selected on the numerics plot

                        const preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => mark.type !== "waveformMark");
                        const newXAnnotationMarks = [
                            ...preExistingMarks,
                            {
                                type: 'waveformMark',
                                from: clickedTimestamp,
                                to: clickedTimestamp + 60,
                                label: 'Waveform',
                            },
                        ];
                        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
                        globalThis.xAnnotationMarksForWaveforms = newXAnnotationMarks;
                    }

                    u.over.oncontextmenu = (e: any) => {
                        let clickedTimestamp = u.posToVal(e.offsetX, "x");
                        // Round to nearest 60 because they have to start on the minute
                        clickedTimestamp = Math.round(clickedTimestamp / 60) * 60
                        handleShowContextMenu(e, true, clickedTimestamp);
                    }
                }
            ],
        },
    }
}

export function getECGNumericOpts(plotWidthRef: any, numeric: string, idx: number, handleShowContextMenu: any, plotWidth: number | null, minMax: any, updateWaveformPreview: any, patientVisit: any, showXAxis?: boolean) {
    let seriesOpts = [
        {}, // Time
        {
            // paths: () => null, // Disables line
            stroke: PLOT_BLUE,
            dash: [1, 3], 
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: PLOT_BLUE,
            },
        }, // Charted Vital Signs #1
        {
            // paths: () => null, // Disables line
            stroke: PLOT_BLUE,
            dash: [1, 3], 
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: PLOT_BLUE,
            },
        }, // Charted Vital Signs #2
        {
            stroke: PLOT_BLUE,
            points: {
                // There doesn't appear to be a way to get infrequent points to show up 
                // but hide more frequent points because infrequent points are surrounded
                // by 'null' values and the 'space' parameter doesn't seem to care what
                // values are in the dataset when determining what points to show.
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
                // show: true,
                // space: 1,
            },
        }, // Monitor Vital Signs #1
        {
            stroke: PLOT_BLUE,
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Monitor Vital Signs #2
        {
            paths: () => null, // Disables line
            stroke: 'red',
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: "red",
            },
        }, // Abnormal Charted Vital Signs #1
        {
            paths: () => null, // Disables line
            stroke: 'red',
            width: 2,
            points: {
                show: true,
                space: 0,
                fill: "red",
            },
        }, // Abnormal Charted Vital Signs #2
        {
            stroke: 'red',
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Abnormal Monitor Vital Signs #1
        {
            stroke: 'red',
            points: {
                show: (self: any, i: any, i0: any, i1: any, _gaps: any) => {
                    return showPointsInCaseOfOverlappingGaps(_gaps);
                },
            },
        }, // Abnormal Monitor Vital Signs #2
    ];
    
    return {
        id: `${idx}-${numeric}`,
        width: plotWidth ? plotWidth : plotWidthRef.current,
        height: showXAxis ? PLOT_HEIGHT + 40 : PLOT_HEIGHT,
        legend: {
            show: false
        },
        plugins: [
            verticalAnnotationsPluginForNumerics({
                types: {
                    eventMark: {
                        show: true,
                        width: 2,
                        align: 'btm',
                        stroke: 'rgb(45 181 181)',
                        fill: 'rgb(45 181 181 / 20%)',
                        dash: [5, 5],
                    },
                    waveformMark: {
                        show: true,
                        width: 2,
                        align: 'btm',
                        stroke: 'rgb(76 175 80)',
                        fill: 'rgb(76 175 80 / 20%)',
                        dash: [5, 5],
                    },
                },
                showAnnotationLabel: true,
            }),
            tooltipNumericPlugin(),
            // wheelZoomPlugin({factor: 0.75}),
        ],
        cursor: {
            sync: {
                key: 'numerics',
            },
        },
        scales: {
            x: {
                time: true,
                // If the plot is zoomed in, we want to retain the zoom on any changes to the plot
                // such as the addition of new annotations.
                //
                // @ts-ignore
                min: globalThis.zoomedRange ? globalThis.zoomedRange.min : minMax.min,
                // @ts-ignore
                max: globalThis.zoomedRange ? globalThis.zoomedRange.max : minMax.max,
            },
        },
        series: seriesOpts,
        axes: [
            {
                show: true,
                font: showXAxis ? "12px Arial" : "0px Arial",
                labelSize: showXAxis ? 30 : 0,
                size: showXAxis ? 50 : 4,
            },
            {
                show: true,
                // label: numeric,
                labelGap: 8,
            },
        ],
        hooks: {
            init: [
                (u: any) => {
                    u.over.onmouseup = (e: any) => {

                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }
                    }
                    u.over.onclick = (e: any) => {
                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }

                        let clickedTimestamp = u.posToVal(e.offsetX, 'x');
                        updateWaveformPreview(new Date(clickedTimestamp * 1000));

                        // Move the waveform preview to where the user selected on the numerics plot

                        const preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => mark.type !== "waveformMark");
                        const newXAnnotationMarks = [
                            ...preExistingMarks,
                            {
                                type: 'waveformMark',
                                from: clickedTimestamp,
                                to: clickedTimestamp + 60,
                                label: 'Waveform',
                            },
                        ];
                        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
                        globalThis.xAnnotationMarksForWaveforms = newXAnnotationMarks;
                    }

                    u.over.oncontextmenu = (e: any) => {
                        let clickedTimestamp = u.posToVal(e.offsetX, "x");
                        // Round to nearest 60 because they have to start on the minute
                        clickedTimestamp = Math.round(clickedTimestamp / 60) * 60
                        handleShowContextMenu(e, true, clickedTimestamp);
                    }
                }
            ],
        },
    }
}

export function getWaveformAvailabilityOpts(addAnnotationToPlots: any, handleShowContextMenu: any, plotWidth: number, patientVisit?: PatientVisit, tenant?: string) {
    return {
        id: `waveform-availability`,
        width: plotWidth,
        height: PLOT_HEIGHT,
        legend: {
            show: false
        },
        cursor: {
            // y: false,
            // points: {
            //     show: false,
            // },
            // drag: {
            //     setScale: false,
            //     x: false, 
            //     y: false,
            // },
            sync: {
                // key: 'availability',
                key: 'numerics',
            },
        },
        plugins: [
            verticalAnnotationsPluginForWaveforms({
                types: {
                    eventMark: {
                        show: false,
                    },
                    waveformMark: {
                        show: true,
                        width: 2,
                        align: 'btm',
                        stroke: 'rgb(76 175 80)',
                        fill:   'rgb(76 175 80 / 20%)',
                        dash: [5, 5],
                    },
                },
                showAnnotationLabel: false,
            }),
            tooltipWaveformAvailabilityPlugin(),
        ],
        scales: {
            x: {
                time: true,
                // If the plot is zoomed in, we want to retain the zoom on any changes to the plot
                // such as the addition of new annotations.
                //
                // @ts-ignore
                min: globalThis.zoomedRange ? globalThis.zoomedRange.min : getEarliestTime(patientVisit) / 1000,
                // @ts-ignore
                max: globalThis.zoomedRange ? globalThis.zoomedRange.max : getLatestTime(patientVisit, tenant) / 1000,
            },
            y: {
                range: [-0.25, 2.25],
            }
        },
        series: [{}, 
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_BLUE, // fill color on mouseover
                },
                stroke: PLOT_BLUE,
                width: 1,
            }, // II
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_GRAY, // fill color on mouseover
                },
                stroke: PLOT_GRAY,
                width: 1,
            }, // II (insufficient quality)
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_BLUE, // fill color on mouseover
                },
                stroke: PLOT_BLUE,
                width: 1,
            }, // Pleth
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_GRAY, // fill color on mouseover
                },
                stroke: PLOT_GRAY,
                width: 1,
            }, // Pleth (insufficient quality)
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_BLUE, // fill color on mouseover
                },
                stroke: PLOT_BLUE,
                width: 1,
            }, // Resp
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_GRAY, // fill color on mouseover
                },
                stroke: PLOT_GRAY,
                width: 1,
            }, // Resp (insufficient quality)
            {
                paths: () => null, // Disables line
                points: {
                    show: true,
                    space: 0,
                    fill: PLOT_RED, // fill color on mouseover
                },
                stroke: PLOT_RED,
                width: 1,
            }, // II
        ],
        axes: [{}, {
            label: "Availability",
            incrs: [
                1
            ],
            space: 10,
            values: (u: any, t: any) => t.map((v: number) => {
                if (v === 0) {
                    return "Resp";
                } else if (v === 1) {
                    return "Pleth";
                } else if (v === 2) {
                    return "II";
                } else {
                    return v;
                }
            }),
        }],
        hooks: {
            init: [
                (u: any) => {
                    u.over.onclick = (e: any) => {
                        // @ts-ignore
                        globalThis.zoomedRange = {
                            min: u.scales.x.min,
                            max: u.scales.x.max,
                        }

                        let clickedTimestamp = u.posToVal(e.offsetX, 'x');
                        addAnnotationToPlots(clickedTimestamp, patientVisit, "waveformMark", "Waveform", 60);
                    }

                    u.over.oncontextmenu = (e: any) => {
                        let clickedTimestamp = u.posToVal(e.offsetX, "x");
                        // Round to nearest 60 because they have to start on the minute
                        clickedTimestamp = Math.round(clickedTimestamp / 60) * 60
                        handleShowContextMenu(e, true, clickedTimestamp);
                    }
                }
            ],
        },
    }
}

export function getWaveformOpts(waveform: string, plotWidth: number) {
    let axesOpts: any[] = [
        {},
        {
            label: waveform,
            labelGap: 8,
        }
    ];

    return {
        id: waveform,
        width: plotWidth,
        height: PLOT_HEIGHT,
        legend: {
            show: false
        },
        cursor: {
            sync: {
                key: 'waveform',
            },
        },
        scales: {
            x: {
                time: true,
            },
            y: {
                auto: true,
            }
        },
        series: [{}, 
            {
                stroke: PLOT_BLUE,
            }
        ],
        axes: axesOpts,
        plugins: [
            tooltipNumericPlugin(),
        ],
    };
}

export function getAvailableNumerics(currPatientVisit: PatientVisit, filterOptions: any) {
    return PREFERRED_NUMERICS_ORDER.filter((numeric: string) => filterOptions["numerics"].includes(numeric));
}

export function getAvailableWaveforms(currPatientVisit: PatientVisit, filterOptions: any) {
    return PREFERRED_WAVEFORMS_ORDER.filter((waveform: string) => filterOptions["waveforms"].includes(waveform));
}

export function convertToTempUnit(inputAlignedData: uPlot.AlignedData, tempUnit: string): uPlot.AlignedData {
    if (tempUnit === "C") {
        // Data is assumed to be in F
        return inputAlignedData;
    } else {
        let alignedData = inputAlignedData.map(function(arr: any) {
            return arr.slice();
        });
        for (let i = 1; i < alignedData.length; i++) {
            if (alignedData[i]) {
                for (let j = 0; j < alignedData[i].length; j++) {
                    if (alignedData[i][j]) {
                        alignedData[i][j] = ((alignedData[i][j] as number) * 9/5) + 32
                    }
                }
            }
        }
        return alignedData as uPlot.AlignedData;
    }
}
